"use client";
import { useEffect, useState, useContext, useMemo } from "react";
import axios from "./config/axios";
import { AuthContext } from "./context/AuthContext";
import SiteChrome from "./components/SiteChrome";
import { CurrencyDisplay } from "./utils/currency";

interface Product {
  _id: string;
  name: string;
  description?: string;
  price: number;
  images?: string;
  category?: string;
  stock?: number;
  status?: string;
  featuredMessage?: string;
}

export default function MainPage() {
  const auth = useContext(AuthContext);
  const isLoggedIn = !!auth?.user;

  const [homeHtml, setHomeHtml] = useState<string>("");
  const [homeLoading, setHomeLoading] = useState<boolean>(true);
  const [products, setProducts] = useState<Product[]>([]);

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const res = await axios.get("/cms/render?templates=home");
        const home = res.data?.templates?.find((t: any) => t.type === "home");
        setHomeHtml(home?.content || "");
      } catch (err) {
        console.error("Failed to load home template", err);
      } finally {
        setHomeLoading(false);
      }
    };
    fetchTemplate();
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;
    axios
      .get("/products")
      .then((res) => {
        const data = res.data?.products ? res.data.products : res.data;
        setProducts(Array.isArray(data) ? data : []);
      })
      .catch((err) => console.error("Failed to load products", err));
  }, [isLoggedIn]);

  const featuredProducts = useMemo(() => {
    const available = products.filter((p) => p.status === "active" && (p.stock ?? 0) > 0);
    // Prefer items with a featuredMessage, then any active
    const featured = available.filter((p) => (p.featuredMessage || "").trim());
    const pool = featured.length > 0 ? featured : available;
    return pool.slice(0, 4);
  }, [products]);

  return (
    <SiteChrome>
      {/* CMS-driven hero (banners, video, whatever the store owner ships) */}
      {homeLoading ? (
        <div className="w-full h-[40vh] bg-gray-100 animate-pulse" />
      ) : homeHtml ? (
        <section
          className="[&_section]:mb-0 [&_img]:block [&_video]:block"
          dangerouslySetInnerHTML={{ __html: homeHtml }}
        />
      ) : null}

      {/* CTA band — sits below the CMS hero, adapts to auth state */}
      <section className="border-y border-gray-200 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-10 md:py-14 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
            {isLoggedIn ? "Ready to place an order?" : "Wholesale, made simple."}
          </h2>
          <p className="text-sm md:text-base text-gray-600 mb-6 max-w-xl mx-auto">
            {isLoggedIn
              ? "Browse the full catalog and add products in bulk. Your saved addresses and cart are one click away."
              : "Sign in to your wholesale account or apply for one — pricing and full catalog unlock instantly."}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {isLoggedIn ? (
              <>
                <a
                  href="/shop"
                  className="inline-flex justify-center items-center px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-sm transition"
                >
                  Browse catalog
                </a>
                <a
                  href="/shop/cart"
                  className="inline-flex justify-center items-center px-6 py-3 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold transition"
                >
                  View cart
                </a>
              </>
            ) : (
              <>
                <a
                  href="/login"
                  className="inline-flex justify-center items-center px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-sm transition"
                >
                  Sign in
                </a>
                <a
                  href="/shop/register"
                  className="inline-flex justify-center items-center px-6 py-3 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold transition"
                >
                  Apply for wholesale
                </a>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Featured products (only when logged in) */}
      {isLoggedIn && featuredProducts.length > 0 && (
        <section className="bg-gray-50 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-12">
            <div className="flex items-end justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">Featured</h2>
                <p className="text-sm text-gray-500">Quick picks from the catalog</p>
              </div>
              <a href="/shop" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                View full catalog →
              </a>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 tabular-nums">
              {featuredProducts.map((p) => (
                <a
                  key={p._id}
                  href="/shop"
                  className="block bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-blue-400 hover:shadow-sm transition"
                >
                  {p.images && (
                    <img
                      src={p.images}
                      alt={p.name}
                      className="w-full aspect-square object-cover"
                      loading="lazy"
                    />
                  )}
                  <div className="p-3">
                    <div className="text-sm font-medium text-gray-800 truncate" title={p.name}>{p.name}</div>
                    {p.category && (
                      <div className="text-xs text-gray-500 mt-0.5">{p.category}</div>
                    )}
                    <div className="mt-2 text-sm font-semibold text-gray-800">
                      {p.price ? <CurrencyDisplay amount={p.price} /> : "—"}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}
    </SiteChrome>
  );
}
